import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { useApolloClient } from '@apollo/client';
import {
  ALL,
  buildExportClaimsDrawerSections,
  exportClaimsExcelColumnWidths,
  exportClaimsExcelHeaderGroups,
  exportClaimsExcelHeaders,
  exportClaimsExcelStyles,
  IExportClaimsFilters
} from '@common/constants';
import { useDrawerSubmission } from '@common/hooks';
import {
  getClaimDetailsForExport,
  searchClaimsForExport
} from '@common/services';
import { useGetExportClaimsLOVs } from '@common/slices';
import { IBaseDrawerProps } from '@common/types';
import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import FormattingService from '@core/services/FormattingService';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { downloadExcelFile } from '@utils/excel/download-json-excel-service';

const buildExportClaimsLovs = (lovData: any) => {
  const claimStatuses =
    lovData?.claimManagement?.getClaimsLovs?.claimStatuses || [];
  const policies = lovData?.production?.searchPolicies?.items || [];

  const Status: Record<string, string> = {
    [ALL]: 'All'
  };
  claimStatuses.forEach((s: any) => {
    if (s?.id) Status[String(s.id)] = s.value ?? s.id;
  });

  const Policy: Record<string, string> = {
    [ALL]: 'All'
  };
  policies.forEach((p: any) => {
    if (p?.id) Policy[String(p.id)] = p.policyNumber ?? p.id;
  });

  return { Status, Policy };
};

const normalizeExportClaimsMulti = (v: any): string[] => {
  if (!v) return [];

  return Array.isArray(v) ? v : [v];
};

const fetchAllClaimsForExport = async (
  client: any,
  query: any,
  vars: {
    statuses: string[];
    policies: string[];
    claimDateStart: string;
    claimDateEnd: string;
    declarationDateStart: string;
    declarationDateEnd: string;
    createdOnStart: string;
    createdOnEnd: string;
  }
) => {
  const pageSize = 500;
  let currentPage = 1;
  const all: any[] = [];
  let hasMore = true;

  while (hasMore) {
    const { data } = await client.query({
      query,
      fetchPolicy: 'no-cache',
      variables: { currentPage, currentPageSize: pageSize, ...vars }
    });

    const items = data?.claimManagement?.getClaims?.items || [];
    all.push(...items);

    hasMore = items.length === pageSize;
    currentPage += 1;
  }

  return all;
};

const fetchClaimDetailsInBatches = async (
  client: any,
  claimIds: string[],
  batchSize = 20
): Promise<Record<string, any>> => {
  const detailsMap: Record<string, any> = {};

  for (let i = 0; i < claimIds.length; i += batchSize) {
    const batch = claimIds.slice(i, i + batchSize);
    const results = await Promise.all(
      batch.map(async (id) => {
        try {
          const { data } = await client.query({
            query: getClaimDetailsForExport,
            fetchPolicy: 'no-cache',
            variables: { id }
          });
          return { id, details: data?.claimManagement?.getClaimDetailsById };
        } catch {
          return { id, details: null };
        }
      })
    );
    results.forEach(({ id, details }) => {
      detailsMap[id] = details;
    });
  }

  return detailsMap;
};

const filterClaimsByPolicy = (
  items: any[],
  selectedPolicies: string[] | null
) => {
  if (!selectedPolicies || selectedPolicies.length === 0) return items;

  return items.filter((c) =>
    selectedPolicies.includes(String(c?.claim?.policy?.id ?? ''))
  );
};

const parseNumber = (value: unknown): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return Number(value.replace(/,/g, '')) || 0;
  return 0;
};

const sumCoverField = (coverDetails: any[], field: string): number =>
  (coverDetails ?? []).reduce((acc, r) => acc + parseNumber(r?.[field]), 0);

const formatAmount = (value: number, symbol: string): string => {
  if (!value) return `${symbol} 0`;
  return `${symbol} ${Number(value.toFixed(3)).toString()}`;
};

const mapClaimsToExcelData = (
  items: any[],
  claimDetailsMap: Record<string, any>
) =>
  items.map((c: any) => {
    const claimId = c?.claim?.id ?? '';
    const details = claimDetailsMap[claimId];
    const coverDetails = details?.claimCoverDetails ?? [];

    const currencySymbol =
      details?.claimDetails?.claimCurrency?.symbol ??
      c?.claim?.policy?.policyCurrency?.symbol ??
      '';

    return {
      policyNumber: c?.claim?.policy?.policyNumber ?? '',
      policyStatus:
        FormattingService.formatEnumToDisplay(c?.claim?.policy?.policyStatus) ??
        '',
      policyEffectiveDate: c?.claim?.policy?.policyEffectiveDate ?? '',
      policyExpiryDate: c?.claim?.policy?.policyExpiryDate ?? '',
      policyIssueDate: c?.claim?.policy?.policyIssueDate ?? '',
      currency: c?.claim?.policy?.policyCurrency?.title ?? '',
      claimNumber: c?.claim?.claimNumber ?? '',
      claimDate: c?.claim?.claimDate ?? '',
      declarationDate: c?.claim?.declarationDate ?? '',
      createdOn: c?.claim?.createdOn ?? '',
      status: FormattingService.formatEnumToDisplay(c?.claim?.status) ?? '',
      statusReason: c?.claim?.statusReason ?? '',
      insured: [
        c?.claim?.insured?.firstName,
        c?.claim?.insured?.middleName,
        c?.claim?.insured?.lastName
      ]
        .filter(Boolean)
        .join(' '),
      notes: details?.claimDetails?.claimNotes ?? '',
      totalEstimations: formatAmount(
        sumCoverField(coverDetails, 'estimationClaim'),
        currencySymbol
      ),
      totalApproved: formatAmount(
        sumCoverField(coverDetails, 'approvedAmountClaim'),
        currencySymbol
      ),
      totalPaid: formatAmount(
        sumCoverField(coverDetails, 'paidAmountClaim'),
        currencySymbol
      )
    };
  });

const ExportClaimsDrawer: React.FC<IBaseDrawerProps> = ({ onSuccess }) => {
  const client = useApolloClient();
  const { submitButtonState, formDisabled, handleSubmission } =
    useDrawerSubmission();

  const [values, setValues] = useState<IExportClaimsFilters>({
    policy: [ALL],
    status: [ALL],
    claimDateRange: [],
    declarationDateRange: [],
    createdOnDateRange: []
  });

  const [lovs, setLovs] = useState<Record<string, Record<string, string>>>({});
  const [sections, setSections] = useState<IDynamicFormWidgetSection[]>([]);

  const { data: lovData } = useGetExportClaimsLOVs.useQuery();

  useEffect(() => {
    const built = buildExportClaimsLovs(lovData);
    setLovs(built);
    setSections(buildExportClaimsDrawerSections(values, built));
  }, [lovData]);

  const submitForm = () => {
    const performSubmission = async (): Promise<void> => {
      const [data] = normalizeDynamicFormDrawerValues(sections, values);
      const policies = normalizeExportClaimsMulti(data.policy);
      const statuses = normalizeExportClaimsMulti(data.status);

      const claimDateRange = data.claimDateRange;
      const declarationDateRange = data.declarationDateRange;
      const createdOnDateRange = data.createdOnDateRange;

      const items = await fetchAllClaimsForExport(
        client,
        searchClaimsForExport,
        {
          statuses: statuses.includes(ALL) ? null : statuses,
          policies: policies.includes(ALL) ? null : policies,
          claimDateStart: claimDateRange[0] ? claimDateRange[0] + 'T00:00:00.000Z' : null,
          claimDateEnd: claimDateRange[1] ? claimDateRange[1] + 'T00:00:00.000Z' : null,
          declarationDateStart: declarationDateRange[0] ? declarationDateRange[0] + 'T00:00:00.000Z' : null,
          declarationDateEnd: declarationDateRange[1] ? declarationDateRange[1] + 'T00:00:00.000Z' : null,
          createdOnStart: createdOnDateRange[0] ? createdOnDateRange[0] + 'T00:00:00.000Z' : null,
          createdOnEnd: createdOnDateRange[1] ? createdOnDateRange[1] + 'T00:00:00.000Z' : null
        }
      );

      if (!items.length) {
        toast.error(
          <ToastErrorMessage>
            No claims found for the selected filters
          </ToastErrorMessage>
        );

        return;
      }

      const claimIds = items
        .map((c) => c?.claim?.id)
        .filter(Boolean) as string[];

      const claimDetailsMap = await fetchClaimDetailsInBatches(
        client,
        claimIds
      );

      const excelData = mapClaimsToExcelData(items, claimDetailsMap);

      downloadExcelFile({
        data: excelData,
        filename: 'Redbridge_Claims',
        headers: exportClaimsExcelHeaders,
        sheetName: 'Sheet1',
        groupHeaders: exportClaimsExcelHeaderGroups,
        styles: exportClaimsExcelStyles,
        columnWidths: exportClaimsExcelColumnWidths,
        freezeHeaderRows: 2,
        headerRowHeight: 14,
        groupHeaderRowHeight: 18,
        separator: { enabled: true, applyTo: 'headerOnly', style: 'medium' }
      });

      toast.success(
        <ToastSuccessMessage>Claims successfully exported</ToastSuccessMessage>
      );
    };

    handleSubmission(
      performSubmission,
      () => onSuccess?.(),
      (error) => {
        console.error('Export error:', error);
        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
      }
    );
  };

  const handleChange = (
    fieldName: keyof IExportClaimsFilters,
    value: any,
    _sectionIndex: number,
    updatedValues: IExportClaimsFilters
  ) => {
    const newValues = { ...updatedValues, [fieldName]: value };
    setValues(newValues);
    setSections(buildExportClaimsDrawerSections(newValues, lovs));
  };

  return (
    <DynamicFormWidget
      onChange={handleChange}
      sections={sections}
      disableForm={formDisabled}
      submitButtonState={submitButtonState}
      onSubmit={submitForm}
    />
  );
};

export { ExportClaimsDrawer };
